import { IbePriceModelCode } from '@gecko-akh/pkg-cruise-api-types/src/iris'
import { IFlight as VivienIFlight } from '@gecko-akh/pkg-cruise-api-types/src/vivien/pdf_offer'
import {
  CLASSIC,
  CLASSICAI,
  COMFORTAI,
  IND,
  INDAI,
  LIGHT,
  PAUSCHAL,
  PAUSCHALAI,
  PRICE_MODEL_SORT_ORDER,
} from '../../general/master-data'
import { IMapCruiseGridResult, IPrice } from '../cruise-list/interfaces'
import { IOutboundInboundFlexFlight } from './flex-flight/interfaces'
import { IAirportData, IExpinetFlightType, IFlight } from './flight/interfaces'
import { FlightClass, FlightClassEnum } from './interfaces'

export const availablePriceModels = (cruise: IMapCruiseGridResult): IbePriceModelCode[] => {
  const priceModels: IbePriceModelCode[] = []

  for (const priceModel of PRICE_MODEL_SORT_ORDER) {
    const priceModelPricings = cruise[priceModel.toLocaleLowerCase() as keyof IMapCruiseGridResult] as IPrice

    if (!!priceModelPricings?.categories) {
      priceModels.push(priceModel)
    }
  }

  return priceModels
}

export const mapPriceModelLabel = (priceModelCode: IbePriceModelCode) => {
  switch (priceModelCode) {
    case COMFORTAI:
      return 'COMFORT ALL IN'
    case INDAI:
      return 'PREMIUM ALL IN'
    case IND:
      return 'PREMIUM'
    case CLASSICAI:
      return 'CLASSIC ALL IN'
    case CLASSIC:
      return 'CLASSIC'
    case PAUSCHALAI:
      return 'PAUSCHAL ALL IN'
    case PAUSCHAL:
      return 'PAUSCHAL'
    case LIGHT:
      return 'LIGHT'
    default:
      return priceModelCode
  }
}

export const mapPriceModelLabelForTracking = (priceModelCode: IbePriceModelCode) => {
  switch (priceModelCode) {
    case COMFORTAI:
      return 'COMFORTAI'
    case INDAI:
      return 'PREMIUMAI'
    case IND:
      return 'PREMIUM'
    case CLASSICAI:
      return 'CLASSICAI'
    case CLASSIC:
      return 'CLASSIC'
    case PAUSCHALAI:
      return 'PAUSCHALAI'
    case PAUSCHAL:
      return 'PAUSCHAL'
    case LIGHT:
      return 'LIGHT'
    default:
      return priceModelCode
  }
}

export const flightClassName = (flightClass: FlightClass): string => {
  switch (flightClass) {
    case FlightClassEnum.Y:
      return 'Economy'
    case FlightClassEnum.P:
      return 'Premium Economy'
    case FlightClassEnum.C:
      return 'Economy Class'
    case FlightClassEnum.F:
      return 'First Class'
    case FlightClassEnum.B:
      return 'Business'
    default:
      return flightClass
  }
}

export const getFlightObjectForVivien = (
  flight?: IExpinetFlightType | IOutboundInboundFlexFlight | null,
  isOutbound?: boolean
): VivienIFlight | undefined => {
  if (!flight) return

  const departure = flight.departure
  const stopDeparture = flight.stopDeparture
  const arrival = flight.arrival
  const stopArrival = flight.stopArrival
  const feeder = flight.feederFlight

  const hasFeederFlight = !!stopArrival && !!stopDeparture && !!feeder

  const mainDeparture = isOutbound || !stopDeparture ? departure : stopDeparture
  const feederDeparture = isOutbound && !!stopDeparture ? stopDeparture : departure
  const mainArrival = isOutbound && !!stopArrival ? stopArrival : arrival
  const feederArrival = isOutbound || !stopArrival ? arrival : stopArrival

  const mainFlight = getFlightDetails(flight.mainFlight, mainDeparture, mainArrival)
  const feederFlight = hasFeederFlight ? getFlightDetails(feeder, feederDeparture, feederArrival) : undefined

  return {
    mainFlight,
    feederFlight,
  }
}

const getFlightDetails = (
  flight: IFlight | null,
  departureDestination: IAirportData,
  arrivalDestination: IAirportData
) => {
  const airline = flight?.airline

  return {
    airline: {
      id: airline?.id ?? '',
      name: airline?.name ?? '',
      operatedBy: airline?.operatedByName ?? '',
    },
    flightClass: {
      name: flight?.flightClass.name,
    },
    departure: {
      airportId: departureDestination.airportId,
      time: departureDestination.time,
      airportName: departureDestination.airportName,
    },
    arrival: {
      airportId: arrivalDestination.airportId,
      time: arrivalDestination.time,
      airportName: arrivalDestination.airportName,
    },
    flightNumber: !!airline?.flightNumber && !!airline.id ? `${airline.id} ${airline.flightNumber}` : '',
  }
}

export const detailsHelper = {
  getFlightObjectForVivien,
}

// ---------------- Export for testing purposes only ----------------

export const exportForTesting = {
  getFlightDetails,
}
